import { useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Loader2, CheckCircle2 } from "lucide-react";
import { useAppModal } from "@/context/AppModalContext";
import StoreBadges from "@/components/site/StoreBadges";
import { trackEvent } from "@/lib/analytics";
import { useLeadForm } from "@/hooks/useLeadForm";
import { SITE } from "@/constants/testIds";

export default function GetAppModal() {
  const { open, setOpen, trigger } = useAppModal();
  const { email, setEmail, status, errorMsg, submit, reset } = useLeadForm({
    source: "get-the-app-modal",
    placement: "modal",
  });

  useEffect(() => {
    if (!open) {
      const t = setTimeout(reset, 200);
      return () => clearTimeout(t);
    }
    trackEvent("get_app_modal_open", { trigger });
  }, [open, trigger, reset]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="sm:max-w-md bg-white p-0 overflow-hidden"
        data-testid={SITE.modalRoot}
      >
        <div className="p-6 sm:p-8">
          <DialogHeader className="text-left">
            <DialogTitle
              className="font-heading text-2xl sm:text-3xl font-bold tracking-tight"
              style={{ color: "var(--yn-ink)" }}
            >
              Get the app
            </DialogTitle>
            <DialogDescription className="text-[color:var(--yn-muted)] text-base">
              Install YeboNadi on the phone you actually carry.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-6">
            <StoreBadges
              placement="modal"
              appTestId={SITE.modalAppStoreBadge}
              playTestId={SITE.modalPlayStoreBadge}
            />
          </div>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-[color:var(--yn-border)]" />
            <span className="text-xs uppercase tracking-wider text-[color:var(--yn-muted)]">
              or
            </span>
            <div className="h-px flex-1 bg-[color:var(--yn-border)]" />
          </div>

          {status === "success" ? (
            <div
              className="rounded-md border border-[color:var(--yn-border)] bg-[color:var(--yn-subtle)] p-4 flex items-start gap-3"
              data-testid={SITE.modalSuccess}
              role="status"
              aria-live="polite"
            >
              <CheckCircle2
                size={20}
                className="mt-0.5 shrink-0"
                style={{ color: "var(--yn-primary)" }}
              />
              <div>
                <p className="text-sm font-semibold text-[color:var(--yn-ink)]">
                  Check your inbox.
                </p>
                <p className="text-sm text-[color:var(--yn-muted)] mt-1">
                  We sent the App Store and Google Play links to <strong>{email}</strong>.
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" noValidate>
              <label
                htmlFor="get-app-email"
                className="block text-sm font-medium text-[color:var(--yn-ink)]"
              >
                Get protected, we&apos;ll send the link to your phone.
              </label>
              <Input
                id="get-app-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                data-testid={SITE.modalEmailInput}
                className="h-11 border-[color:var(--yn-border)] focus-visible:ring-[color:var(--yn-primary)]"
              />
              {status === "error" && (
                <p
                  className="text-sm text-red-600"
                  data-testid={SITE.modalError}
                  role="alert"
                >
                  {errorMsg}
                </p>
              )}
              <button
                type="submit"
                disabled={status === "loading"}
                className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-md text-white text-sm font-semibold bg-[color:var(--yn-primary)] hover:bg-[color:var(--yn-primary-hover)] transition-colors disabled:opacity-70"
                data-testid={SITE.modalEmailSubmit}
              >
                {status === "loading" && <Loader2 size={16} className="animate-spin" />}
                {status === "loading" ? "Sending…" : "Send me the link"}
              </button>
              <p className="text-xs text-[color:var(--yn-muted)] leading-relaxed">
                We&apos;ll only use your email to send the app link and important product
                updates. No spam.
              </p>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
